/**
 * How a conversation is LABELLED. Pure functions, shared by the Live Conversations list, the
 * message thread's header and the floating chat dock — one place, so the three can never disagree
 * about what a chat is called.
 *
 * <p>The rule that matters: a WhatsApp conversation is keyed by the customer's phone number, so
 * falling back to {@code waId} reads perfectly. A WEB_WIDGET conversation is keyed by a SESSION
 * UUID, so the same fallback would print `6966c855-c5e7-447b-…` at the user. For a widget we show
 * the number its journey captured ({@code contactPhone}) or nothing at all.</p>
 */

/** True for a web-widget session. The channel is authoritative; the id shape is a fallback for rows
 *  whose channel isn't known yet (no messages loaded). */
export function isWidgetConv(conv: any): boolean {
  if ((conv?.channel || '').toUpperCase() === 'WEB_WIDGET') return true;
  return !/^[0-9+][0-9]*$/.test(String(conv?.waId || ''));
}

/** The identifier worth SHOWING, or null when there isn't one. Never a raw session UUID. */
export function convDisplayId(conv: any): string | null {
  const phone = (conv?.contactPhone || '').trim();
  if (phone) return phone;
  const waId = String(conv?.waId || '').trim();
  if (!waId) return null;
  return isWidgetConv(conv) ? null : waId;
}

/** Headline: the captured name, else the number, else a neutral label. */
export function convTitle(conv: any): string {
  return (conv?.name || '').trim() || convDisplayId(conv) || 'Web visitor';
}

/** Secondary line: the number, but only when it isn't already the headline. */
export function convSubtitle(conv: any): string | null {
  const id = convDisplayId(conv);
  return id && id !== convTitle(conv) ? id : null;
}

/** Up to two initials for the avatar. Falls back to the last two characters of a REAL id — a
 *  widget session's UUID has no meaningful tail, which is why it goes through convDisplayId. */
export function convInitials(conv: any): string {
  const name = (conv?.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
  }
  const id = convDisplayId(conv);
  return id ? id.slice(-2) : '?';
}
